import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, output, signal, ViewChild } from '@angular/core';
import { AppComponentBaseDirective } from '@app/shared/directives/app-component-base.directive';
import { BankIdAuthAutoStartModel, BankIdAuthenticateResultModel, BankIdPendingType, PatientTokenAuthServiceProxy } from '@shared/service-proxies/service-proxies';
import { exhaustMap, finalize, interval, race, Subject, takeUntil } from 'rxjs';

const MAX_TRY_COUNT = 13;

@Component({
  selector: 'nw-bankid-signing-modal',
  templateUrl: './bankid-signing-modal.component.html',
  styleUrl: './bankid-signing-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankidSigningModalComponent extends AppComponentBaseDirective implements OnDestroy {

  modalSave = output<BankIdAuthenticateResultModel>();
  modalClosed = output<void>();

  private tokenAuthService = inject(PatientTokenAuthServiceProxy);
  private modalHide$ = new Subject<void>();
  tryCount = 0;
  authResponse?: BankIdAuthAutoStartModel;
  hidden = signal<'true' | 'false'>('true');
  qrCodeImage = signal<string | undefined>(undefined);


  show() {
    this.startLogin();
  }

  startLogin() {
    this.tryCount = 0;
    this.authResponse = undefined;
    this.tokenAuthService.bankIdLoginStart().subscribe(result => {
      this.authResponse = result;
      this.qrCodeImage.set(result.qrBase64Image);
      this.hidden.set('false');
      this.pollForAuthResult();
    });
  }

  close() {
    this.hide();
    this.modalClosed.emit();
  }

  hide() {
    this.hidden.set('true');
    this.modalHide$.next();
  }

  openOnThisDevice() {}

  pollForAuthResult() {
    //const unsubscribe$ = new Subject<void>();
    interval(2000).pipe(
      exhaustMap(_ => this.tokenAuthService.bankIdCollectResponse(this.authResponse!.orderRef)),
      takeUntil(this.modalHide$)
    ).subscribe({
      next: result => {
        this.tryCount++;
        switch (result.pendingType) {
          case BankIdPendingType.Success:
            this.modalSave.emit(result);
            this.hide();
            break;
          case BankIdPendingType.Pending:
            this.qrCodeImage.set(result.qrBase64Image);
            if (this.tryCount > MAX_TRY_COUNT) {
              abp.notify.error(this.l('BankIdSigningWaitTimeout'));
              this.hide();
              this.modalClosed.emit();
            }
            break;
          case BankIdPendingType.Error:
            this.hide();
            this.message.confirm(this.l('BankIdStartAuthAgain'),
              this.l('UnexpectedErrorOccurredAndMessageIs') + result.pendingMessage,
              (result) => {
                if (result) {
                  this.show();
                } else this.modalClosed.emit();
              }, { showCancelButton: false });
            break;
          case BankIdPendingType.AccountNotExist:
            this.hide();
            this.message.confirm(this.l('BankIdStartAccountNotExist'), this.l('TryAgain'), (result) => {
              if (result) {
                this.show();
              } else this.modalClosed.emit();
            });
            break;
          default:
            this.hide();
            this.message.confirm(this.l('EmailNotConfirmed'), this.l('TryAgain'), (result) => {
              if (result) {
                this.show();
              } else this.modalClosed.emit();
            });

            break;
        }
      },
      error: (e) => {
        console.error('Polling error: ', e);
      }
    });
  }

  ngOnDestroy(): void {
    this.modalHide$.complete();
  }
}
